/**
 * PricingPaymentsPanel — unified Admin → Pricing & Payments console.
 *
 * Sections: App billing mode + currency · Payment provider switches ·
 * mode-aware Catalog editor (products in one-off, tiers in subscription).
 * Everything reads/writes the single pricing source of truth; price changes
 * propagate to marketing/SEO automatically (a hint banner reminds the operator).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import PricingHistoryPanel from "@/components/admin/PricingHistoryPanel";
import {
  Loader2, Save, CreditCard, ToggleLeft, Tag, Info, CheckCircle2, AlertTriangle, Lock, Plus, Trash2,
  ArrowUp, ArrowDown, X, Linkedin,
} from "lucide-react";

export default function PricingPaymentsPanel() {
  const [settings, setSettings] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    try {
      const { data: s } = await api.get("/admin/billing/settings");
      setSettings(s);
      const isOneTime = s.billing_mode === "one_time";
      const { data } = await api.get(isOneTime ? "/admin/products" : "/admin/tiers");
      setRows(isOneTime ? data.products : data.tiers);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load pricing");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  if (loading || !settings) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;

  const isOneTime = settings.billing_mode === "one_time";

  return (
    <div className="space-y-8" data-testid="pricing-payments-panel">
      <ModeAndProviders settings={settings} onChanged={reload} />
      <CutoverPanel />
      <CatalogEditor isOneTime={isOneTime} rows={rows} onChanged={reload} processorName={settings.providers?.processor_name || "Payment Processor"} />
      <PricingHistoryPanel products={rows} refreshKey={rows} />
      <SeoSyncPanel />
    </div>
  );
}

function CutoverPanel() {
  const [value, setValue] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings");
        setValue(data?.paywall_effective_from || "");
      } catch {
        setValue("");
      }
    })();
  }, []);

  if (value === null) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;

  const on = !!value.trim();
  const date = /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : "";
  const tm = value.match(/T(\d{2}:\d{2})/);
  const time = tm ? tm[1] : "";
  const setCutover = (d, t) => setValue(!d ? "" : `${d}T${t || "00:00"}:00Z`);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/settings", { paywall_effective_from: value });
      toast.success(on ? "Paywall cutover saved." : "Paywall turned off.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5" data-testid="cutover-panel">
      <h3 className="font-display text-lg font-semibold text-slate-900 dark:text-slate-50 flex items-center gap-2">
        <Lock className="h-5 w-5 text-amber-600 dark:text-amber-400" /> Monetisation cutover
      </h3>
      <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
        Leave blank to keep the paywall <b>off</b> (no watermarks, all exports free). Pick a date (UTC) to switch on the DRAFT watermark + upgrade flow for every assessment from that moment onwards.
      </p>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Cutover date (UTC)</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setCutover(e.target.value, time)}
            data-testid="admin-paywall-cutover-date"
            className="mt-1.5 h-10 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-600"
          />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Time (UTC, optional)</label>
          <input
            type="time"
            value={time}
            disabled={!date}
            onChange={(e) => setCutover(date, e.target.value)}
            data-testid="admin-paywall-cutover-time"
            className="mt-1.5 h-10 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-600 disabled:opacity-50"
          />
        </div>
        {on && (
          <button
            type="button"
            onClick={() => setValue("")}
            data-testid="admin-paywall-clear"
            className="h-10 px-3 rounded-md border border-slate-300 dark:border-slate-700 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
          >
            Clear (turn off)
          </button>
        )}
        <button
          onClick={save}
          disabled={saving}
          data-testid="admin-paywall-save"
          className="h-10 px-4 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 disabled:opacity-50 transition-colors inline-flex items-center gap-1.5"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save cutover
        </button>
      </div>
      <p className={`mt-3 inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md ${
        on
          ? "bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-200 border border-amber-200 dark:border-amber-900/60"
          : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700"
      }`} data-testid="admin-paywall-status">
        <span className={`h-1.5 w-1.5 rounded-full ${on ? "bg-amber-500" : "bg-slate-400 dark:bg-slate-500"}`} />
        {on ? `Cutover: ${value}` : "Paywall disabled"}
      </p>
    </section>
  );
}

function SeoSyncPanel() {
  const [status, setStatus] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/billing/seo-sync");
      setStatus(data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await api.post("/admin/billing/seo-sync/refresh");
      toast.success("Structured data (JSON-LD) refreshed from the catalog.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  if (!status) return null;
  const inSync = status.jsonld_in_sync;
  return (
    <section className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5" data-testid="seo-sync-panel">
      <h3 className="font-display text-lg font-semibold text-slate-900 dark:text-slate-50 flex items-center gap-2">
        <Info className="h-5 w-5 text-teal-600" /> SEO / AEO price accuracy
      </h3>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
        The JSON-LD <code className="font-mono text-xs">Product</code> / <code className="font-mono text-xs">Offer</code> schema is what Google AI Overviews, ChatGPT, Perplexity and Claude read to state your price. It auto-refreshes whenever you change a product price.
      </p>
      <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
        <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${inSync ? "text-emerald-700 dark:text-emerald-400" : "text-amber-700 dark:text-amber-400"}`} data-testid="seo-sync-state">
          {inSync ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
          {inSync ? "Structured data matches your catalog prices" : "Structured data may be stale — refresh recommended"}
        </span>
        <button
          onClick={refresh}
          disabled={refreshing}
          data-testid="seo-sync-refresh"
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-teal-600 text-white text-sm font-medium hover:bg-teal-500 disabled:opacity-50 transition-colors"
        >
          {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Info className="h-3.5 w-3.5" />}
          Refresh structured data from catalog
        </button>
      </div>
    </section>
  );
}

function ModeAndProviders({ settings, onChanged }) {
  const [mode, setMode] = useState(settings.billing_mode);
  const [currency, setCurrency] = useState(settings.currency);
  const [providers, setProviders] = useState(settings.providers);
  const [procName, setProcName] = useState(settings.providers?.processor_name || "Payment Processor");
  const [savingName, setSavingName] = useState(false);
  const [saving, setSaving] = useState(false);

  const displayName = providers.processor_name || "Payment Processor";

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put("/admin/billing/settings", { billing_mode: mode, currency });
      toast.success("Billing settings saved.");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const saveProcessorName = async () => {
    const name = (procName || "").trim();
    if (!name) { toast.error("Enter a name for the payment processor."); return; }
    setSavingName(true);
    try {
      const { data } = await api.put("/admin/payments-config", { processor_name: name });
      setProviders(data.providers);
      setProcName(data.providers.processor_name);
      toast.success("Payment processor name saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingName(false);
    }
  };

  const toggleProvider = async (key) => {
    const next = { ...providers, [key]: !providers[key] };
    try {
      const { data } = await api.put("/admin/payments-config", { [key]: next[key] });
      setProviders(data.providers);
      toast.success(`${key === "paypal_enabled" ? "PayPal" : displayName} ${data.providers[key] ? "enabled" : "disabled"}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <section className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
      <h3 className="font-display text-lg font-semibold text-slate-900 dark:text-slate-50 flex items-center gap-2">
        <ToggleLeft className="h-5 w-5 text-teal-600" /> App billing setup
      </h3>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <p className="text-[11px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400 mb-2">Billing mode</p>
          <div className="flex gap-2">
            {[["one_time", "One-off products"], ["subscription", "Subscription tiers"]].map(([v, l]) => (
              <button
                key={v}
                onClick={() => setMode(v)}
                data-testid={`billing-mode-${v}`}
                className={`flex-1 h-10 px-3 rounded-md border text-sm font-medium transition-colors ${
                  mode === v
                    ? "border-teal-600 bg-teal-50 dark:bg-teal-500/15 text-teal-800 dark:text-teal-200"
                    : "border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-3">
            <label className="text-xs text-slate-500 dark:text-slate-400">Currency</label>
            <input
              value={currency}
              onChange={(e) => setCurrency(e.target.value.toUpperCase())}
              data-testid="billing-currency"
              className="h-9 w-24 px-2 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm"
            />
            <button
              onClick={saveSettings}
              disabled={saving}
              data-testid="billing-settings-save"
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 disabled:opacity-50 transition-colors"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
            </button>
          </div>
        </div>

        <div>
          <p className="text-[11px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400 mb-2">Payment providers</p>
          <div className="space-y-2">
            <ProviderToggle label={displayName} on={providers.lunipay_enabled} onClick={() => toggleProvider("lunipay_enabled")} testid="pay-switch-lunipay" />
            <ProviderToggle
              label={`PayPal${settings.paypal_configured ? "" : " (add credentials first)"}`}
              on={providers.paypal_enabled}
              disabled={!settings.paypal_configured}
              onClick={() => toggleProvider("paypal_enabled")}
              testid="pay-switch-paypal"
            />
          </div>
          <div className="mt-3">
            <label className="block text-[10px] uppercase tracking-wide text-slate-400 mb-1">Payment processor name (shown to customers)</label>
            <div className="flex items-center gap-2">
              <input
                value={procName}
                onChange={(e) => setProcName(e.target.value)}
                placeholder="e.g. eZee Payments"
                data-testid="processor-name-input"
                className="flex-1 h-9 px-2 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm"
              />
              <button
                onClick={saveProcessorName}
                disabled={savingName}
                data-testid="processor-name-save"
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 disabled:opacity-50 transition-colors"
              >
                {savingName ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
              </button>
            </div>
            <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
              This is the secondary hosted-checkout processor. Paste its payment link on each product below and enable the toggle. Customers see &ldquo;Pay on {displayName}&rdquo;.
            </p>
          </div>
          {!settings.paypal_configured && (
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
              <Info className="h-3.5 w-3.5" /> PayPal webhook URL to register: <code className="font-mono">/api/webhooks/paypal</code>
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

function ProviderToggle({ label, on, disabled, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`w-full flex items-center justify-between h-11 px-3 rounded-md border text-sm transition-colors ${
        disabled ? "opacity-50 cursor-not-allowed border-slate-200 dark:border-slate-800"
          : "border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800"
      }`}
    >
      <span className="flex items-center gap-2 text-slate-800 dark:text-slate-100"><CreditCard className="h-4 w-4" /> {label}</span>
      <span className={`inline-flex h-6 w-11 items-center rounded-full px-0.5 transition-colors ${on ? "bg-teal-600 justify-end" : "bg-slate-300 dark:bg-slate-700 justify-start"}`}>
        <span className="h-5 w-5 rounded-full bg-white" />
      </span>
    </button>
  );
}

function CatalogEditor({ isOneTime, rows, onChanged, processorName }) {
  const [reordering, setReordering] = useState(false);

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= rows.length) return;
    const arr = [...rows];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setReordering(true);
    try {
      await Promise.all(
        arr
          .map((r, i) => {
            const want = (i + 1) * 10;
            return r.sort_order === want ? null : api.put(`/admin/products/${r.slug}`, { sort_order: want });
          })
          .filter(Boolean)
      );
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reorder");
    } finally {
      setReordering(false);
    }
  };

  return (
    <section>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-display text-lg font-semibold text-slate-900 dark:text-slate-50 flex items-center gap-2">
          <Tag className="h-5 w-5 text-teal-600" /> {isOneTime ? "Product catalog" : "Subscription tiers"}
        </h3>
      </div>
      <div className="mt-2 rounded-lg border border-amber-300 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-3 text-sm text-amber-900 dark:text-amber-100 flex items-start gap-2">
        <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
        <span>This catalog is the single source of truth. Editing a price here updates checkout, on-page labels, and the marketing AI prompts automatically.{isOneTime ? " Use the ↑ / ↓ arrows to set the order products appear on the public Pricing page (2 per row)." : ""}</span>
      </div>
      <div className="mt-4 space-y-4">
        {rows.map((r, i) => (
          <CatalogRow
            key={r.slug}
            row={r}
            index={i}
            total={rows.length}
            isOneTime={isOneTime}
            onChanged={onChanged}
            processorName={processorName}
            onMove={isOneTime ? move : null}
            reordering={reordering}
          />
        ))}
      </div>
      {isOneTime && <AddProductForm onChanged={onChanged} />}
    </section>
  );
}

function AddProductForm({ onChanged }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [d, setD] = useState({ display_name: "", price: "", price_label: "", public_description: "", lunipay_payment_link_url: "", paypal_button_link: "", features_text: "", linkedin_enabled: false, linkedin_url: "", linkedin_name: "", linkedin_title: "", linkedin_photo_url: "", linkedin_label: "" });

  const create = async () => {
    if (!d.display_name.trim() || !Number(d.price)) { toast.error("Enter a product name and a price."); return; }
    setSaving(true);
    try {
      const features = (d.features_text || "").split("\n").map((s) => s.trim()).filter(Boolean);
      await api.post("/admin/products", {
        display_name: d.display_name, price: Number(d.price), price_label: d.price_label,
        public_description: d.public_description, lunipay_payment_link_url: d.lunipay_payment_link_url,
        paypal_button_link: d.paypal_button_link, features,
        linkedin_enabled: !!d.linkedin_enabled, linkedin_url: d.linkedin_url,
        linkedin_name: d.linkedin_name, linkedin_title: d.linkedin_title,
        linkedin_photo_url: d.linkedin_photo_url, linkedin_label: d.linkedin_label,
      });
      toast.success(`"${d.display_name}" added to the catalogue.`);
      setD({ display_name: "", price: "", price_label: "", public_description: "", lunipay_payment_link_url: "", paypal_button_link: "", features_text: "", linkedin_enabled: false, linkedin_url: "", linkedin_name: "", linkedin_title: "", linkedin_photo_url: "", linkedin_label: "" });
      setOpen(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add product");
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="catalog-add-open"
        className="mt-4 inline-flex items-center gap-1.5 h-10 px-4 rounded-md border border-dashed border-teal-400 dark:border-teal-500/50 text-teal-700 dark:text-teal-300 text-sm font-medium hover:bg-teal-50 dark:hover:bg-teal-500/10 transition-colors"
      >
        <Plus className="h-4 w-4" /> Add a product
      </button>
    );
  }

  const NF = ({ label, k, type = "text", w }) => (
    <div className={w || ""}>
      <label className="block text-[10px] uppercase tracking-wide text-slate-400 mb-1">{label}</label>
      <input
        type={type}
        value={d[k]}
        onChange={(e) => setD({ ...d, [k]: e.target.value })}
        data-testid={`new-product-${k}`}
        className="w-full h-9 px-2 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm"
      />
    </div>
  );

  return (
    <div className="mt-4 rounded-lg border border-teal-300 dark:border-teal-500/40 bg-teal-50/50 dark:bg-teal-500/5 p-4" data-testid="catalog-add-form">
      <p className="text-sm font-semibold text-slate-900 dark:text-slate-50 mb-3">New product</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <NF label="Display name" k="display_name" w="col-span-2" />
        <NF label="Price (USD)" k="price" type="number" />
        <NF label="Price label" k="price_label" />
        <NF label="Public description" k="public_description" w="col-span-2 md:col-span-4" />
        <NF label="Payment processor link" k="lunipay_payment_link_url" w="col-span-2" />
        <NF label="PayPal link" k="paypal_button_link" w="col-span-2" />
        <div className="col-span-2 md:col-span-4">
          <label className="block text-[10px] uppercase tracking-wide text-slate-400 mb-1">Feature bullets (one per line)</label>
          <textarea
            value={d.features_text}
            onChange={(e) => setD({ ...d, features_text: e.target.value })}
            rows={4}
            placeholder={"Clean, watermark-free PDF\nBranded logo on the cover"}
            data-testid="new-product-features"
            className="w-full px-2 py-2 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm"
          />
        </div>
        <div className="col-span-2 md:col-span-4">
          <label className="flex items-center gap-2 text-sm text-slate-800 dark:text-slate-100">
            <input
              type="checkbox"
              checked={!!d.linkedin_enabled}
              onChange={(e) => setD({ ...d, linkedin_enabled: e.target.checked })}
              data-testid="new-product-linkedin-enabled"
            />
            <Linkedin className="h-4 w-4 text-[#0a66c2]" /> Add a LinkedIn profile card
          </label>
          {d.linkedin_enabled && (
            <div className="mt-2 space-y-2">
              <input
                value={d.linkedin_url}
                onChange={(e) => setD({ ...d, linkedin_url: e.target.value })}
                placeholder="LinkedIn profile URL (https://linkedin.com/in/…)"
                data-testid="new-product-linkedin-url"
                className="w-full h-9 px-2 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm"
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  value={d.linkedin_name}
                  onChange={(e) => setD({ ...d, linkedin_name: e.target.value })}
                  placeholder="Name"
                  data-testid="new-product-linkedin-name"
                  className="w-full h-9 px-2 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm"
                />
                <input
                  value={d.linkedin_title}
                  onChange={(e) => setD({ ...d, linkedin_title: e.target.value })}
                  placeholder="Title / headline"
                  data-testid="new-product-linkedin-title"
                  className="w-full h-9 px-2 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm"
                />
              </div>
              <input
                value={d.linkedin_photo_url}
                onChange={(e) => setD({ ...d, linkedin_photo_url: e.target.value })}
                placeholder="Photo URL (optional)"
                data-testid="new-product-linkedin-photo"
                className="w-full h-9 px-2 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm"
              />
              <input
                value={d.linkedin_label}
                onChange={(e) => setD({ ...d, linkedin_label: e.target.value })}
                placeholder="Caption, e.g. Led by"
                data-testid="new-product-linkedin-label"
                className="w-full h-9 px-2 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm"
              />
            </div>
          )}
        </div>
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <button onClick={() => setOpen(false)} data-testid="catalog-add-cancel" className="h-9 px-4 rounded-md border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-sm hover:bg-slate-50 dark:hover:bg-slate-800">Cancel</button>
        <button
          onClick={create}
          disabled={saving}
          data-testid="catalog-add-save"
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 disabled:opacity-50 transition-colors"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add product
        </button>
      </div>
    </div>
  );
}

function CatalogRow({ row, isOneTime, onChanged, processorName, index, total, onMove, reordering }) {
  const [draft, setDraft] = useState(row);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDraft(row); }, [row]);

  const save = async () => {
    setSaving(true);
    try {
      const endpoint = isOneTime ? `/admin/products/${row.slug}` : `/admin/tiers/${row.slug}`;
      const payload = isOneTime
        ? {
            display_name: draft.display_name, public_description: draft.public_description,
            price: Number(draft.price), price_label: draft.price_label, active: draft.active,
            lunipay_payment_link_url: draft.lunipay_payment_link_url, paypal_button_link: draft.paypal_button_link,
            features: (draft.features || []).map((s) => (s || "").trim()).filter(Boolean),
            linkedin_enabled: !!draft.linkedin_enabled,
            linkedin_url: draft.linkedin_url || "",
            linkedin_name: draft.linkedin_name || "",
            linkedin_title: draft.linkedin_title || "",
            linkedin_photo_url: draft.linkedin_photo_url || "",
            linkedin_photo_path: draft.linkedin_photo_path || "",
            linkedin_label: draft.linkedin_label || "",
          }
        : { display_name: draft.display_name, description: draft.description, allowance: Number(draft.allowance), is_paid: draft.is_paid, active: draft.active, lunipay_payment_link_url: draft.lunipay_payment_link_url, paypal_plan_id: draft.paypal_plan_id, paypal_button_link: draft.paypal_button_link };
      const { data } = await api.put(endpoint, payload);
      if (isOneTime && data.row) setDraft(data.row);
      if (data.price_changed) toast.success(`${draft.display_name} saved — marketing/SEO will reflect the new price.`);
      else toast.success(`${draft.display_name} saved.`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const F = ({ label, k, type = "text", w }) => (
    <div className={w || ""}>
      <label className="block text-[10px] uppercase tracking-wide text-slate-400 mb-1">{label}</label>
      <input
        type={type}
        value={draft[k] ?? ""}
        onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
        data-testid={`catalog-${k}-${row.slug}`}
        className="w-full h-9 px-2 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm"
      />
    </div>
  );

  const PROTECTED = ["brief", "advisory"];
  const canDelete = isOneTime && !PROTECTED.includes(row.slug);
  const deleteRow = async () => {
    if (!window.confirm(`Delete "${row.display_name}"? This removes it from the catalogue.`)) return;
    try {
      await api.delete(`/admin/products/${row.slug}`);
      toast.success(`${row.display_name} deleted.`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const features = draft.features || [];
  const setFeature = (i, val) => { const nf = [...features]; nf[i] = val; setDraft({ ...draft, features: nf }); };
  const addFeature = () => setDraft({ ...draft, features: [...features, ""] });
  const removeFeature = (i) => setDraft({ ...draft, features: features.filter((_, x) => x !== i) });

  const [photoUploading, setPhotoUploading] = useState(false);
  const liPhoto = draft.linkedin_photo_url || (draft.linkedin_photo_path ? `${API}/blogs/media/${draft.linkedin_photo_path}` : "");
  const uploadPhoto = async (file) => {
    if (!file) return;
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/blogs/upload-cover", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDraft((d) => ({ ...d, linkedin_photo_path: data.cover_path, linkedin_photo_url: "" }));
      toast.success("Photo uploaded — Save to apply");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setPhotoUploading(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4" data-testid={`catalog-row-${row.slug}`}>
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="flex items-center gap-2">
          {onMove && (
            <div className="flex items-center gap-0.5">
              <button
                type="button"
                onClick={() => onMove(index, -1)}
                disabled={index === 0 || reordering}
                data-testid={`catalog-move-up-${row.slug}`}
                title="Move up"
                className="p-1.5 rounded-md border border-slate-200 dark:border-slate-700 text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ArrowUp className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => onMove(index, 1)}
                disabled={index === total - 1 || reordering}
                data-testid={`catalog-move-down-${row.slug}`}
                title="Move down"
                className="p-1.5 rounded-md border border-slate-200 dark:border-slate-700 text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ArrowDown className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          <span className="font-mono text-xs px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">{row.slug}</span>
        </div>
        <label className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
          <input type="checkbox" checked={!!draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} data-testid={`catalog-active-${row.slug}`} /> Active
        </label>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <F label="Display name" k="display_name" w="col-span-2" />
        {isOneTime ? (
          <>
            <F label="Price" k="price" type="number" />
            <F label="Price label" k="price_label" />
            <F label="Public description" k="public_description" w="col-span-2 md:col-span-4" />
            <F label={`${processorName} link`} k="lunipay_payment_link_url" w="col-span-2" />
            <F label="PayPal link" k="paypal_button_link" w="col-span-2" />

            {/* Feature bullets — shown on the public pricing card */}
            <div className="col-span-2 md:col-span-4 border-t border-slate-100 dark:border-slate-800 pt-3">
              <label className="block text-[10px] uppercase tracking-wide text-slate-400 mb-2">Feature bullets (shown on the pricing card)</label>
              <div className="space-y-2">
                {features.map((f, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      value={f}
                      onChange={(e) => setFeature(i, e.target.value)}
                      data-testid={`catalog-feature-${row.slug}-${i}`}
                      className="flex-1 h-9 px-2 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => removeFeature(i)}
                      data-testid={`catalog-feature-remove-${row.slug}-${i}`}
                      className="p-2 text-rose-400 hover:text-rose-600 transition-colors"
                      title="Remove bullet"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={addFeature}
                data-testid={`catalog-feature-add-${row.slug}`}
                className="mt-2 inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-dashed border-teal-400 dark:border-teal-500/50 text-teal-700 dark:text-teal-300 text-xs font-medium hover:bg-teal-50 dark:hover:bg-teal-500/10 transition-colors"
              >
                <Plus className="h-3.5 w-3.5" /> Add bullet
              </button>
            </div>

            {/* Optional LinkedIn block */}
            <div className="col-span-2 md:col-span-4 border-t border-slate-100 dark:border-slate-800 pt-3">
              <label className="flex items-center gap-2 text-sm text-slate-800 dark:text-slate-100">
                <input
                  type="checkbox"
                  checked={!!draft.linkedin_enabled}
                  onChange={(e) => setDraft({ ...draft, linkedin_enabled: e.target.checked })}
                  data-testid={`catalog-linkedin-enabled-${row.slug}`}
                />
                <Linkedin className="h-4 w-4 text-[#0a66c2]" /> Show a LinkedIn profile card on this product
              </label>
              {draft.linkedin_enabled && (
                <div className="mt-3 space-y-2">
                  <div>
                    <label className="block text-[10px] uppercase tracking-wide text-slate-400 mb-1">Caption (above the card)</label>
                    <input
                      value={draft.linkedin_label ?? ""}
                      onChange={(e) => setDraft({ ...draft, linkedin_label: e.target.value })}
                      placeholder="e.g. Your workshop is led by"
                      data-testid={`catalog-linkedin-label-${row.slug}`}
                      className="w-full h-9 px-2 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-wide text-slate-400 mb-1">
                      LinkedIn profile URL <span className="text-rose-500">*</span>
                    </label>
                    <input
                      value={draft.linkedin_url ?? ""}
                      onChange={(e) => setDraft({ ...draft, linkedin_url: e.target.value })}
                      placeholder="https://www.linkedin.com/in/their-handle"
                      data-testid={`catalog-linkedin-url-${row.slug}`}
                      className="w-full h-9 px-2 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm"
                    />
                    {draft.linkedin_vanity && (
                      <p className="mt-1 text-[11px] text-slate-400">Handle: <b>{draft.linkedin_vanity}</b> (detected after save)</p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] uppercase tracking-wide text-slate-400 mb-1">Name</label>
                      <input
                        value={draft.linkedin_name ?? ""}
                        onChange={(e) => setDraft({ ...draft, linkedin_name: e.target.value })}
                        placeholder="Full name"
                        data-testid={`catalog-linkedin-name-${row.slug}`}
                        className="w-full h-9 px-2 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-wide text-slate-400 mb-1">Title / headline</label>
                      <input
                        value={draft.linkedin_title ?? ""}
                        onChange={(e) => setDraft({ ...draft, linkedin_title: e.target.value })}
                        placeholder="e.g. Resilience Advisor"
                        data-testid={`catalog-linkedin-title-${row.slug}`}
                        className="w-full h-9 px-2 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-wide text-slate-400 mb-1">Photo (optional)</label>
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-full overflow-hidden bg-[#0a66c2]/10 border border-slate-200 dark:border-slate-800 flex items-center justify-center shrink-0">
                        {liPhoto ? <img src={liPhoto} alt="" className="h-full w-full object-cover" data-testid={`catalog-linkedin-photo-preview-${row.slug}`} /> : <Linkedin className="h-5 w-5 text-[#0a66c2]" />}
                      </div>
                      <label className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-100 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer transition-colors">
                        {photoUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Upload
                        <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid={`catalog-linkedin-photo-upload-${row.slug}`} onChange={(e) => uploadPhoto(e.target.files?.[0])} />
                      </label>
                      {liPhoto && (
                        <button type="button" onClick={() => setDraft({ ...draft, linkedin_photo_url: "", linkedin_photo_path: "" })} className="text-xs text-rose-500 hover:text-rose-700 inline-flex items-center gap-1">
                          <X className="h-3.5 w-3.5" /> Remove
                        </button>
                      )}
                    </div>
                    <input
                      value={draft.linkedin_photo_url ?? ""}
                      onChange={(e) => setDraft({ ...draft, linkedin_photo_url: e.target.value, linkedin_photo_path: e.target.value ? "" : draft.linkedin_photo_path })}
                      placeholder="…or paste an image URL"
                      data-testid={`catalog-linkedin-photo-url-${row.slug}`}
                      className="mt-2 w-full h-9 px-2 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm"
                    />
                  </div>
                  <p className="text-[11px] text-slate-400">Works for any consultant — paste their LinkedIn URL and we render a clean, theme-matched card (no iframe). Save to apply.</p>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <F label="Allowance" k="allowance" type="number" />
            <F label="Period" k="period" />
            <F label="Description" k="description" w="col-span-2 md:col-span-4" />
            <F label="PayPal plan_id" k="paypal_plan_id" w="col-span-2" />
            <F label="PayPal button link" k="paypal_button_link" w="col-span-2" />
            <F label={`${processorName} link`} k="lunipay_payment_link_url" w="col-span-2 md:col-span-4" />
          </>
        )}
      </div>
      <div className="mt-3 flex justify-end gap-2">
        {canDelete && (
          <button
            onClick={deleteRow}
            data-testid={`catalog-delete-${row.slug}`}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-red-200 dark:border-red-500/40 text-red-700 dark:text-red-300 text-sm font-medium hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
          >
            <Trash2 className="h-3.5 w-3.5" /> Delete
          </button>
        )}
        <button
          onClick={save}
          disabled={saving}
          data-testid={`catalog-save-${row.slug}`}
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 disabled:opacity-50 transition-colors"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
        </button>
      </div>
    </div>
  );
}
